import { formatShortTime } from "@/lib/format/time";
import { cardSurfaceClass, cardTitleTextClass, compactInputClass } from "@/components/common/surfaceClasses";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";
import type { Lead } from "@/types/lead";
import type { LeadTransferCandidate } from "@/lib/lead/transferLeadOwner";
import { LeadProvenanceMeta } from "./LeadProvenanceMeta";

export type LeadsModeRecentLeadsBlockProps = {
  appDisplayName: string;
  recentLeadsOpen: boolean;
  setRecentLeadsOpen: (fn: (v: boolean) => boolean) => void;
  recentFilter: "all" | "today" | "unsent";
  setRecentFilter: (v: "all" | "today" | "unsent") => void;
  recentSort: "recent" | "oldest" | "status";
  setRecentSort: (v: "recent" | "oldest" | "status") => void;
  showLeadsManagerFilters?: boolean;
  showLeadsOwnerFilter?: boolean;
  recentLocationFilter?: string | "all";
  setRecentLocationFilter?: (v: string | "all") => void;
  recentOwnerFilter?: string | "all";
  setRecentOwnerFilter?: (v: string | "all") => void;
  locationOptions?: Array<{ id: string; label: string }>;
  ownerCandidates?: LeadTransferCandidate[];
  ownerLabelByUserId?: Map<string, string>;
  locationNameById?: Map<string, string>;
  visibleRecentLeads: Lead[];
  filteredRecentLeadsCount: number;
  hasMoreLeads: boolean;
  showAllRecentLeads: boolean;
  setShowAllRecentLeads: (fn: (v: boolean) => boolean) => void;
  openLeadInEditor: (lead: Lead) => void;
  deleteLead: (id: string) => void;
  mergeLeadIntoCurrent: (sourceId: string) => void;
  isMergeCandidate: (a: Lead, b: Lead) => boolean;
  formatLeadAge: (ageMs: number) => string;
  currentLeadId: string | null;
  currentLead: Lead | null;
};

export function LeadsModeRecentLeadsBlock(props: LeadsModeRecentLeadsBlockProps) {
  const {
    appDisplayName,
    recentLeadsOpen,
    setRecentLeadsOpen,
    recentFilter,
    setRecentFilter,
    recentSort,
    setRecentSort,
    showLeadsManagerFilters,
    showLeadsOwnerFilter,
    recentLocationFilter = "all",
    setRecentLocationFilter,
    recentOwnerFilter = "all",
    setRecentOwnerFilter,
    locationOptions = [],
    ownerCandidates = [],
    ownerLabelByUserId = new Map(),
    locationNameById = new Map(),
    visibleRecentLeads,
    filteredRecentLeadsCount,
    hasMoreLeads,
    showAllRecentLeads,
    setShowAllRecentLeads,
    openLeadInEditor,
    deleteLead,
    mergeLeadIntoCurrent,
    isMergeCandidate,
    formatLeadAge,
    currentLeadId,
    currentLead,
  } = props;

  return (
    <div className="mt-10 text-left" {...{ [FIREFLY_TARGET_ATTR]: "recent-leads-section" }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setRecentLeadsOpen((v) => !v)}
            className="text-gray-400 hover:text-gray-600 text-base shrink-0"
          >
            {recentLeadsOpen ? "▾" : "▸"}
          </button>
          <h2 className={`text-2xl sm:text-3xl font-bold ${cardTitleTextClass}`}>Recent leads</h2>
        </div>

        {recentLeadsOpen && (
          <div className="flex flex-wrap items-center justify-end gap-2 text-xs sm:text-sm">
            <select
              className={`border rounded-full px-3 py-1 ${compactInputClass}`}
              value={recentFilter}
              onChange={(e) => setRecentFilter(e.target.value as "all" | "today" | "unsent")}
            >
              <option value="all">All</option>
              <option value="today">Today</option>
              <option value="unsent">Unsent</option>
            </select>

            <select
              className={`border rounded-full px-3 py-1 ${compactInputClass}`}
              value={recentSort}
              onChange={(e) => setRecentSort(e.target.value as "recent" | "oldest" | "status")}
            >
              <option value="recent">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="status">Status</option>
            </select>

            {showLeadsManagerFilters && setRecentLocationFilter ? (
              <select
                className={`border rounded-full px-3 py-1 ${compactInputClass}`}
                value={recentLocationFilter}
                onChange={(e) => setRecentLocationFilter(e.target.value)}
                aria-label="Filter by location"
              >
                <option value="all">All locations</option>
                {locationOptions.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.label}
                  </option>
                ))}
              </select>
            ) : null}

            {showLeadsOwnerFilter && setRecentOwnerFilter ? (
              <select
                className={`border rounded-full px-3 py-1 ${compactInputClass}`}
                value={recentOwnerFilter}
                onChange={(e) => setRecentOwnerFilter(e.target.value)}
                aria-label="Filter by user"
              >
                <option value="all">All users</option>
                {ownerCandidates.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.display_name || m.full_name || m.email || m.user_id}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        )}
      </div>

      {recentLeadsOpen && (
        <>
          {visibleRecentLeads.length === 0 ? (
            <p className="text-sm text-gray-500">
              No leads in this view yet. Start a new customer and {appDisplayName} will list them here.
            </p>
          ) : (
            <div className="space-y-3">
              {visibleRecentLeads.map((lead) => {
                const e = lead.extracted || null;
                const createdAtMs = Number(lead.id) || lead.lastUpdated;
                const ageMs = Date.now() - createdAtMs;
                const ageDisplay = formatLeadAge(ageMs);
                const shortTime = formatShortTime(lead.lastUpdated);

                const statusLabel = lead.isTest
                  ? "TEST"
                  : lead.sent
                    ? "Sent"
                    : lead.queued
                      ? "Queued"
                      : "Draft";

                const statusClass =
                  "inline-flex items-center px-2 py-1 rounded-full text-[10px] font-semibold " +
                  (lead.isTest
                    ? "bg-yellow-50 text-yellow-800 border border-yellow-200"
                    : lead.sent
                      ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                      : lead.queued
                        ? "bg-blue-50 text-blue-800 border border-blue-200"
                        : "bg-gray-50 text-gray-700 border border-gray-200");

                const isCurrent = currentLeadId && currentLeadId === lead.id;

                return (
                  <div
                    key={lead.id}
                    className={
                      `relative rounded-2xl border px-4 py-3 shadow-sm flex flex-col sm:flex-row sm:items-center gap-3 ${cardSurfaceClass} ` +
                      (isCurrent ? "border-blue-500 ring-2 ring-blue-100" : "border-gray-200")
                    }
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-sm sm:text-base truncate">
                          {lead.name && lead.name !== "New Customer"
                            ? lead.name
                            : e?.first_name || e?.last_name
                              ? `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim()
                              : "New customer"}
                        </h3>
                        <span className={statusClass}>{statusLabel}</span>
                      </div>

                      <p className="text-xs text-gray-500 mb-1">
                        {e?.product_line || "Sheds"}
                        {e?.product_subtype ? ` • ${e.product_subtype}` : ""}
                        {e?.enunciated_budget ? ` • Budget: ${e.enunciated_budget}` : ""}
                      </p>

                      {showLeadsManagerFilters ? (
                        <LeadProvenanceMeta
                          lead={lead}
                          ownerLabelByUserId={ownerLabelByUserId}
                          locationNameById={locationNameById}
                          showOwner={showLeadsOwnerFilter}
                          compact
                        />
                      ) : null}

                      <p className="text-[11px] text-gray-400">
                        Updated {shortTime} • Age: {ageDisplay}
                      </p>
                    </div>

                    <div className="flex flex-row sm:flex-col gap-2 sm:items-end">
                      <button
                        type="button"
                        onClick={() => openLeadInEditor(lead)}
                        className="px-3 py-1.5 rounded-full text-xs sm:text-sm bg-blue-600 text-white hover:bg-blue-700"
                      >
                        {isCurrent ? "Viewing" : "Open"}
                      </button>

                      {!lead.isTest &&
                        currentLeadId &&
                        currentLeadId !== lead.id &&
                        currentLead &&
                        isMergeCandidate(currentLead, lead) && (
                          <button
                            type="button"
                            onClick={() => mergeLeadIntoCurrent(lead.id)}
                            className="px-3 py-1.5 rounded-full text-xs sm:text-sm bg-gray-100 text-gray-700 hover:bg-gray-200"
                          >
                            Merge into current
                          </button>
                        )}

                      <button
                        type="button"
                        onClick={() => deleteLead(lead.id)}
                        className="px-3 py-1.5 rounded-full text-[11px] sm:text-xs bg-red-50 text-red-700 hover:bg-red-100"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {hasMoreLeads && (
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={() => setShowAllRecentLeads((prev) => !prev)}
                className="text-sm text-blue-700 hover:underline"
              >
                {showAllRecentLeads ? "Show fewer leads" : `Show all ${filteredRecentLeadsCount} leads`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
